/**
 * ConfirmDialog — small in-app confirmation modal (TASK-015 Phase 2), shared
 * by QuizSession.tsx ("Completa quiz" with skipped questions) and
 * QuizHub.tsx ("Scarta" an in-progress quiz). Never `window.confirm` — this
 * codebase already has a house style for that: a fixed-inset backdrop +
 * centered `role="dialog"` panel, same structure as
 * pages/admin/Manuals.tsx's `ConfirmDeleteDialog` and
 * pages/reader/tutor/TutorCitationDialog.tsx.
 *
 * Buttons are hand-rolled in the quiz UI's OWN token language (bg-ink/
 * border-line/rounded-[9px] — the same classes QuizFocus/TestsHub/
 * InProgressCard already use) rather than through
 * `@arianna/design-system`'s `Button`: the quiz surface never reaches for
 * that component anywhere else, so this dialog matches ITS established
 * local convention instead of importing a different one (the same reasoning
 * TutorCitationDialog's own doc comment gives for its buttons).
 */
import * as React from 'react'
import Text from '@arianna/design-system/Text'

export interface ConfirmDialogProps {
  /** Used to wire `aria-labelledby` — must be unique on the page. */
  titleId: string
  title: string
  message: string
  confirmLabel: string
  cancelLabel?: string
  /** Styles the confirm button as a destructive (red) action — e.g. discarding data. */
  destructive?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  titleId,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Annulla',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/45 px-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel()
      }}
    >
      <div className="bg-bg border border-line rounded-lg p-6 max-w-[440px] w-full shadow-2xl font-sans">
        <h2
          id={titleId}
          className="font-serif text-[19px] font-semibold tracking-[-0.01em] leading-tight text-ink m-0 mb-3"
        >
          {title}
        </h2>
        <Text variant="body" className="text-ink-2 mb-5 block">
          {message}
        </Text>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-[10px] text-sm font-medium bg-surface text-ink-2 border border-line rounded-[9px] cursor-pointer font-sans"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={
              destructive ? { background: 'var(--red)', borderColor: 'var(--red)' } : undefined
            }
            className={
              destructive
                ? 'px-[18px] py-[10px] text-sm font-semibold text-bg border rounded-[9px] cursor-pointer font-sans'
                : 'px-[18px] py-[10px] text-sm font-semibold bg-ink text-bg border border-ink rounded-[9px] cursor-pointer font-sans'
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
